import React, { useCallback } from 'react';
import { Flame } from 'lucide-react';
import type { GasMeterSection } from '@officehub/shared';
import { INPUT_CLASS, LABEL_CLASS, TEXTAREA_CLASS } from './styles';
import type { RenderSectionPhotoCapture } from './photoCapture';

export interface GasMeterFormProps {
  data: GasMeterSection;
  onUpdate: (d: Partial<GasMeterSection>) => void;
  /**
   * Optional host-supplied photo capture panel. When omitted the Photos block
   * is not rendered (structured-data-only capture).
   */
  renderPhotoCapture?: RenderSectionPhotoCapture;
}

export function GasMeterForm({
  data,
  onUpdate,
  renderPhotoCapture,
}: GasMeterFormProps): React.ReactElement {
  // A cleared optional input emits null, never undefined. survey_updateSection
  // merges the fields it is given (issue #3230), so only an explicit null
  // deletes a field — an absent key just leaves the stored value alone. The
  // callable SDK happens to encode undefined as null on the wire, but that is
  // an implementation detail of @firebase/functions; saying null here states
  // the intent at the boundary instead of depending on the transport for it.
  const handleChange = useCallback(
    (field: keyof GasMeterSection, value: unknown) => {
      onUpdate({ [field]: value });
    },
    [onUpdate],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-center gap-2 px-1">
        <Flame className="w-3.5 h-3.5 text-slate-400" />
        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">
          Gas Meter
        </h2>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
      </div>

      {/* 2-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Meter Location */}
        <div>
          <label className={LABEL_CLASS}>Meter Location</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={data.meterLocation ?? ''}
            onChange={(e) => handleChange('meterLocation', e.target.value)}
            placeholder="e.g., Right side of house"
          />
        </div>

        {/* Meter Capacity BTU */}
        <div>
          <label className={LABEL_CLASS}>Meter Capacity (BTU/hr)</label>
          <input
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.meterCapacityBTU ?? ''}
            onChange={(e) =>
              handleChange('meterCapacityBTU', e.target.value === '' ? null : Number(e.target.value))
            }
            placeholder="e.g., 250000"
          />
        </div>

        {/* Existing Pressure */}
        <div>
          <label className={LABEL_CLASS}>Existing Pressure</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={data.existingPressure ?? ''}
            onChange={(e) => handleChange('existingPressure', e.target.value)}
            placeholder="e.g., 1/4 PSI"
          />
        </div>

        {/* Distance to Generator Pad */}
        <div>
          <label className={LABEL_CLASS}>Distance to Generator Pad</label>
          <input
            type="number"
            inputMode="numeric"
            className={INPUT_CLASS}
            value={data.distanceToGeneratorPad ?? ''}
            onChange={(e) =>
              handleChange(
                'distanceToGeneratorPad',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
            placeholder="(ft)"
          />
        </div>
      </div>

      {/* Pipe Routing Notes — full width */}
      <div>
        <label className={LABEL_CLASS}>Pipe Routing Notes</label>
        <textarea
          className={TEXTAREA_CLASS}
          rows={3}
          value={data.pipeRoutingNotes ?? ''}
          onChange={(e) => handleChange('pipeRoutingNotes', e.target.value)}
          placeholder="Obstacles, routing path, underground vs overhead…"
        />
      </div>

      {/* Photos — only when the host supplies a capture panel */}
      {renderPhotoCapture && (
        <div>
          <div className="flex items-center gap-2 mb-3 px-1">
            <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.1em]">Photos</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
          </div>
          {renderPhotoCapture({
            section: 'gasMeter',
            photos: data.photos,
            availableTags: [{ value: 'gas_meter', label: 'Gas Meter' }],
          })}
        </div>
      )}
    </div>
  );
}
